import type { OpportunityRelatedDeal } from '~/hooks/apis/offers/useGetOpportunityRelated';

import type { ChecklistStatus, RecordLike } from '../../shared/checklistUtils';
import {
  formatDealOptionName,
  getPackageDisplayName,
  getStatusLabel,
  isFiniteNumber,
  mapOpportunityDealsByPackageId,
  readNumber,
  readString,
} from '../../shared/checklistUtils';

import { minVertexCoverByThreshold } from './minVertexCoverByThreshold';
import type { MinVertexCoverConflict } from './minVertexCoverByThreshold';
import type { DiscountsMarginChecklistRow } from './types';

const DISCOUNTS_MARGIN_FIELDS = {
  offerDiscounts: 'Offer discounts',
} as const;

const DEAL_OFFER_DISCOUNT_FIELD = 'offer_discount__c';
const DEAL_OPTION_MARGIN_SPREAD_THRESHOLD_PERCENT = 50;
const FLEXI_MARGIN_SPREAD_THRESHOLD_PERCENT = 10;

export function buildDiscountsMarginRows({
  opportunityDeals,
  packages,
}: {
  opportunityDeals?: Array<OpportunityRelatedDeal>;
  packages?: Array<App.Package>;
}): Array<DiscountsMarginChecklistRow> {
  const packageRows = packages ?? [];
  const opportunityDealsByPackageId = mapOpportunityDealsByPackageId(opportunityDeals);
  const flexiPackages = packageRows.filter((offerPackage) => !!(offerPackage as unknown as RecordLike).flexible_nights);
  const standardPackages = packageRows.filter(
    (offerPackage) => !(offerPackage as unknown as RecordLike).flexible_nights,
  );

  return [
    buildOfferDiscountsRow({ opportunityDealsByPackageId, packages: packageRows }),
    buildMarginSpreadRow({
      field: 'Deal option margin spread',
      id: 'discounts-margin:deal-option-margin-spread',
      opportunityDealsByPackageId,
      packages: standardPackages,
      thresholdPercent: DEAL_OPTION_MARGIN_SPREAD_THRESHOLD_PERCENT,
    }),
    buildMarginSpreadRow({
      field: 'Flexi deal option margin spread',
      id: 'discounts-margin:flexi-margin-spread',
      opportunityDealsByPackageId,
      packages: flexiPackages,
      thresholdPercent: FLEXI_MARGIN_SPREAD_THRESHOLD_PERCENT,
    }),
  ];
}

function buildOfferDiscountsRow({
  opportunityDealsByPackageId,
  packages,
}: {
  opportunityDealsByPackageId: Map<string, OpportunityRelatedDeal>;
  packages: Array<App.Package>;
}): DiscountsMarginChecklistRow {
  const discountDetails = packages.map((offerPackage) => {
    const packageRecord = offerPackage as unknown as RecordLike;
    const packageId = readString(packageRecord, ['id_salesforce_external']);
    const opportunityDeal = packageId ? opportunityDealsByPackageId.get(packageId) : undefined;
    const dealRecord = (opportunityDeal ?? {}) as unknown as RecordLike;
    const discount = readNumber(dealRecord, [DEAL_OFFER_DISCOUNT_FIELD]);

    return {
      discount,
      name: getPackageDisplayName(offerPackage),
    };
  });
  const hasPackages = discountDetails.length > 0;
  const hasAllDiscounts = discountDetails.every(({ discount }) => isFiniteNumber(discount));
  const hasOutOfRangeDiscount = discountDetails.some(
    ({ discount }) => isFiniteNumber(discount) && (discount < 15 || discount > 75),
  );
  const status: ChecklistStatus =
    hasPackages && hasAllDiscounts && !hasOutOfRangeDiscount ? 'approved' : 'requires approval';

  return {
    details: discountDetails.map(({ discount, name }) => ({
      label: name,
      value: isFiniteNumber(discount) ? `${discount}%` : 'Not available',
    })),
    field: DISCOUNTS_MARGIN_FIELDS.offerDiscounts,
    id: 'discounts-margin:offer-discounts',
    rule: 'If any offer discount is greater than 75% or less than 15%, flag for approval; otherwise auto-approve.',
    sourceValue: hasPackages
      ? discountDetails
          .map(({ discount, name }) => `${name}: ${isFiniteNumber(discount) ? `${discount}%` : 'N/A'}`)
          .join('; ')
      : 'No deal options available',
    status,
    statusLabel: getStatusLabel(status),
    summary:
      hasPackages && hasAllDiscounts && !hasOutOfRangeDiscount
        ? 'Offer discounts are within the approved range.'
        : 'Offer discounts need review.',
    title: DISCOUNTS_MARGIN_FIELDS.offerDiscounts,
  };
}

function getPackageMarginPercent(
  offerPackage: App.Package,
  opportunityDealsByPackageId: Map<string, OpportunityRelatedDeal>,
) {
  const packageId = readString(offerPackage as unknown as RecordLike, ['id_salesforce_external']);
  const opportunityDeal = packageId ? opportunityDealsByPackageId.get(packageId) : undefined;

  return readNumber(opportunityDeal ?? {}, ['margin_percent__c']);
}

interface MarginOutlierEndpoint {
  margin: number;
  name: string;
}

function formatMarginConflictGroupValue(conflictCount: number) {
  return `Margin difference exceeds the threshold with ${conflictCount} other deal option${conflictCount === 1 ? '' : 's'}`;
}

function formatMarginConflictItem(
  conflict: MinVertexCoverConflict<MarginOutlierEndpoint>,
  anchor: MarginOutlierEndpoint,
) {
  const direction = conflict.item.margin > anchor.margin ? 'higher' : 'lower';

  return `${formatDealOptionName(conflict.item.name)} (${conflict.item.margin}%) — ${conflict.delta.toFixed(1)}% ${direction} margin`;
}

function buildMarginSpreadRow({
  field,
  id,
  opportunityDealsByPackageId,
  packages,
  thresholdPercent,
}: {
  field: DiscountsMarginChecklistRow['field'];
  id: string;
  opportunityDealsByPackageId: Map<string, OpportunityRelatedDeal>;
  packages: Array<App.Package>;
  thresholdPercent: number;
}): DiscountsMarginChecklistRow {
  const marginDetails = packages.map((offerPackage) => ({
    margin: getPackageMarginPercent(offerPackage, opportunityDealsByPackageId),
    name: getPackageDisplayName(offerPackage),
  }));
  const comparableMargins = marginDetails.filter((detail): detail is { margin: number; name: string } =>
    isFiniteNumber(detail.margin),
  );
  const hasEnoughOptionsToCompare = comparableMargins.length >= 2;
  const conflictGroups = hasEnoughOptionsToCompare
    ? minVertexCoverByThreshold(comparableMargins, (deal) => deal.margin, thresholdPercent)
    : [];
  const flaggedOptionCount = new Set(
    conflictGroups.flatMap((group) => [group.anchor.name, ...group.conflicts.map((conflict) => conflict.item.name)]),
  ).size;
  const approved = conflictGroups.length === 0;
  const summary = !hasEnoughOptionsToCompare
    ? 'Fewer than 2 deal options with a margin to compare.'
    : approved
      ? 'All deal option margins are within the approved spread.'
      : `Found ${flaggedOptionCount} outlier deal option${flaggedOptionCount === 1 ? '' : 's'}.`;
  const status: ChecklistStatus = approved ? 'approved' : 'requires approval';

  const groupEntries = conflictGroups.map((group) => ({
    label: `${formatDealOptionName(group.anchor.name)} (${group.anchor.margin}%)`,
    value: formatMarginConflictGroupValue(group.conflicts.length),
    expandableItems: group.conflicts.map((conflict) => formatMarginConflictItem(conflict, group.anchor)),
  }));

  return {
    details: groupEntries.length > 0 ? groupEntries : [{ label: 'Outlier pairs', value: 'None' }],
    field,
    id,
    rule: `If the margin spread between any two deal options is ${thresholdPercent}% or more, flag for approval; otherwise auto-approve.`,
    sourceValue:
      groupEntries.length > 0
        ? groupEntries.map(({ label, value }) => `${label}: ${value}`).join('; ')
        : 'No outlier pairs',
    status,
    statusLabel: getStatusLabel(status),
    summary,
    title: field,
  };
}
